import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import LensIcon from "@mui/icons-material/Lens";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

/**
 * Expected fields (numbers where present):
 *  buy_price, buy_price_status
 *  live_price
 *  tp1_price, tp1_price_status
 *  tp2_price, tp2_price_status
 *  tp3_price, tp3_price_status
 *  sl_price,  sl_price_status
 *  sl_price2, sl_price2_status   // optional
 *  sl_price3, sl_price3_status   // optional
 */
export default function PriceProgressLine({ data, width = "100%", height = 60 }) {
  // Build list of points (auto-skip non-numeric)
  const points = useMemo(() => {
    if (!data) return [];

    const addIfNum = (arr, def) => {
      if (typeof def.value === "number" && Number.isFinite(def.value)) arr.push(def);
    };

    const list = [];
    // up to 3 SLs
    addIfNum(list, {
      key: "sl_price",
      label: "SL1",
      title: "Stop Loss 1",
      value: data.sl_price,
      status: data.sl_price_status,
      type: "stop",
    });
    addIfNum(list, {
      key: "sl_price2",
      label: "SL2",
      title: "Stop Loss 2",
      value: data.sl_price2,
      status: data.sl_price2_status,
      type: "stop",
    });
    addIfNum(list, {
      key: "sl_price3",
      label: "SL3",
      title: "Stop Loss 3",
      value: data.sl_price3,
      status: data.sl_price3_status,
      type: "stop",
    });

    // execution, live, tps
    addIfNum(list, {
      key: "buy_price",
      label: "EX",
      title: "Execution Price",
      value: data.buy_price,
      status: data.buy_price_status,
      type: "exec",
    });
    addIfNum(list, {
      key: "live_price",
      label: "LP",
      title: "Live Price",
      value: data.live_price,
      status: "live",
      type: "live",
    });
    addIfNum(list, {
      key: "tp1_price",
      label: "TP1",
      title: "Take Profit 1/3",
      value: data.tp1_price,
      status: data.tp1_price_status,
      type: "tp",
    });
    addIfNum(list, {
      key: "tp2_price",
      label: "TP2",
      title: "Take Profit 2/3",
      value: data.tp2_price,
      status: data.tp2_price_status,
      type: "tp",
    });
    addIfNum(list, {
      key: "tp3_price",
      label: "TP3",
      title: "Take Profit 3/3",
      value: data.tp3_price,
      status: data.tp3_price_status,
      type: "tp",
    });

    // sort by value so left/right detection is accurate and labels look ordered
    return list.sort((a, b) => a.value - b.value);
  }, [data]);

  // Scale always covers all points (even if LP < SL or LP > TP3)
  const { minVal, maxVal, range } = useMemo(() => {
    const vals = points.map(p => p.value);
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 1;
    const r = max - min;
    return { minVal: min, maxVal: max, range: r > 0 ? r : 1 };
  }, [points]);

  const pct = (v) => {
    if (!Number.isFinite(v)) return 0;
    const p = ((v - minVal) / range) * 100;
    return Math.max(0, Math.min(100, p));
  };

  // Colors by type
  const colorFor = (p) => {
    if (p.type === "live") return "#00B050"; // green
    if (p.type === "stop") return "#D9534F"; // red-ish
    if (p.type === "exec") return "#808080"; // gray
    if (p.type === "tp") return "#6F42C1";   // purple
    return "#333";
  };

  // Icon by status
  // - filled -> check
  // - open   -> hollow circle
  // - canceled -> X/stop
  // - live -> solid dot
  const IconFor = (p) => {
    if (p.type === "live") return <LensIcon sx={{ fontSize: 12, color: colorFor(p) }} />;
    if (p.status === "filled") return <CheckCircleIcon sx={{ fontSize: 16, color: colorFor(p) }} />;
    if (p.status === "canceled") return <HighlightOffIcon sx={{ fontSize: 16, color: colorFor(p) }} />;
    // "open" or anything else:
    return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: colorFor(p) }} />;
  };

  const priceText = (val) =>
    `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 8 })}`;

  return (
    <div style={{ width, padding: "8px 0" }}>
      <div style={{ position: "relative", height, display: "flex", alignItems: "center", marginTop: 10 }}>
        {/* baseline */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 4,
            background: "#e0e0e0",
            borderRadius: 4,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />

        {/* ticks + grid lines */}
        {(() => {
          const tickCount = 8; // evenly spaced
          const ticks = Array.from({ length: tickCount }, (_, i) => {
            const t = i / (tickCount - 1);
            const value = minVal + t * range;
            const leftPct = t * 100;
            const isLeft = i === 0;
            const isRight = i === tickCount - 1;
            return {
              i,
              leftPct,
              value,
              transform: isLeft ? "none" : isRight ? "translateX(-100%)" : "translateX(-50%)",
              textAlign: isLeft ? "left" : isRight ? "right" : "center",
            };
          });

          const gridTop = "calc(50% + 2px)";
          const gridHeight = Math.max(0, height / 2 - 20);

          return (
            <>
              {ticks.map(t => (
                <React.Fragment key={`tick-${t.i}`}>
                  <div
                    style={{
                      position: "absolute",
                      left: `${t.leftPct}%`,
                      top: gridTop,
                      height: `${gridHeight}px`,
                      width: 1,
                      background: "rgba(0,0,0,0.3)",
                      transform: "translateX(-0.5px)",
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: `${t.leftPct}%`,
                      top: "calc(50% + 18px)",
                      transform: t.transform,
                      fontSize: 10,
                      color: "#222",
                      opacity: 0.9,
                      whiteSpace: "nowrap",
                      textAlign: t.textAlign,
                      pointerEvents: "none",
                    }}
                  >
                    {priceText(t.value)}
                  </div>
                </React.Fragment>
              ))}
            </>
          );
        })()}

        {/* points */}
        {points.map((p, idx) => {
          const x = pct(p.value);
          const isLeftMost = idx === 0;                // after sorting by value
          const isRightMost = idx === points.length - 1;

          // base caption style
          let captionStyle = {
            position: "absolute",
            fontSize: 11,
            color: colorFor(p),
            whiteSpace: "nowrap",
            textAlign: "center",
          };

          // Vertical placement (you can tweak which ones go above/below)
          // Vertical placement: all above except LP below
          if (p.key === "live_price") {
            captionStyle.bottom = 9;     // LP goes under the line
          } else {
            captionStyle.top = -18;      // everyone else goes above the line
          }

          // Edge alignment rules (leftmost/rightmost)
          if (isLeftMost) {
            captionStyle.left = 0;
            captionStyle.transform = "none";
            captionStyle.textAlign = "left";
          } else if (isRightMost) {
            captionStyle.right = 0;
            captionStyle.transform = "none";
            captionStyle.textAlign = "right";
          }

          return (
            <div
              key={p.key}
              style={{
                position: "absolute",
                left: `${x}%`,
                top: 0,
                height: "100%",
                transform: "translateX(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pointerEvents: "none",
              }}
            >
              {/* short label above the line */}
              <div style={{ fontFamily: "monospace", fontSize: 12, color: colorFor(p), marginBottom: 2 }}>
                {p.label}
              </div>

              {/* icon centered on the line */}
              <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)" }}>
                <Tooltip
                  title={`${p.title}: ${priceText(p.value)}${
                    p.type !== "live" ? ` (${p.status || "open"})` : ""
                  }`}
                  placement="top"
                  arrow
                >
                  <span style={{ pointerEvents: "auto" }}>{IconFor(p)}</span>
                </Tooltip>
              </div>

              {/* price caption (colored to match) */}
              <div style={captionStyle}>{priceText(p.value)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
